import {
  Box,
  Divider,
  SxProps,
  Typography,
  TypographyOwnProps,
  styled,
} from "@mui/material";
import { Fragment, ReactElement, ReactNode } from "react";

import theme, { palette } from "~frontend/theme";

import { FlexBox } from "./FlexBox";

const DEFAULT_LABEL_WIDTH = 140;

export const DetailRow = ({
  children,
  sx = {},
}: {
  children: ReactNode;
  sx?: SxProps;
}): ReactElement => {
  return (
    <FlexBox
      gap={theme.spacing(2)}
      alignItems="center"
      sx={{ ...sx, padding: `${theme.spacing(1)} 0` }}
    >
      {children}
    </FlexBox>
  );
};

export const DetailLabel = ({
  children,
  width = DEFAULT_LABEL_WIDTH,
  variant = "body1Bold",
}: {
  children: ReactNode;
  width?: string | number;
  variant?: TypographyOwnProps["variant"];
}): ReactElement => (
  <Typography
    variant={variant}
    sx={{ width, minWidth: width, wordWrap: "break-word" }}
  >
    {children}
  </Typography>
);

export const DetailValue = styled(Box)({
  width: "100%",
  overflow: "auto",
});

export const LightDivider = styled(Divider)({
  borderColor: palette.lightGray,
});

export type TwoColumnDisplayRow = {
  key: string;
  label: ReactNode;
  content?: ReactNode;
  sx?: SxProps;
  visible?: boolean;
};

export const TwoColumnDisplay = ({
  rows,
  labelWidth = DEFAULT_LABEL_WIDTH,
  labelVariant = "body1Bold",
  allRowSx = {},
  dividerColor = palette.lightGray,
  showTopBorder = true,
  showBottomBorder = true,
  hideDividers = false,
}: {
  rows: TwoColumnDisplayRow[];
  labelWidth?: number | string;
  labelVariant?: TypographyOwnProps["variant"];
  allRowSx?: SxProps;
  dividerColor?: string;
  showTopBorder?: boolean;
  showBottomBorder?: boolean;
  hideDividers?: boolean;
}): ReactElement => {
  return (
    <Box>
      {showTopBorder && !hideDividers && (
        <LightDivider key="top-divider" sx={{ borderColor: dividerColor }} />
      )}
      {rows.map((row, idx) => {
        if (row.visible === false) {
          return null;
        }

        return (
          <Fragment key={row.key}>
            <DetailRow sx={{ ...allRowSx, ...(row.sx || {}) } as SxProps}>
              <DetailLabel width={labelWidth} variant={labelVariant}>
                {row.label}
              </DetailLabel>
              <DetailValue>{row.content}</DetailValue>
            </DetailRow>
            {idx < rows.length - 1 && !hideDividers && (
              <LightDivider sx={{ borderColor: dividerColor }} />
            )}
          </Fragment>
        );
      })}
      {showBottomBorder && !hideDividers && (
        <LightDivider key="bottom-divider" sx={{ borderColor: dividerColor }} />
      )}
    </Box>
  );
};
